import React from 'react';
import {v4 as uuid} from 'uuid';

import {useStateWithDeps} from 'app/utils/hookUtils';

import {cartStorageKey, readCart, writeCart} from './storage';
import {CartItemV1, CartV1, MAX_CART_ITEMS, SceneKeyParts, emptyCart, sceneKey} from './types';
import {CartContext, CartContextValue, CartItemPatch, CartItemRestore} from './useCart';

const newItem = (item: Omit<CartItemV1, 'id' | 'addedAt'>): CartItemV1 => ({
  ...item,
  id: uuid(),
  addedAt: new Date().toISOString(),
});

const indexOfScene = (items: CartItemV1[], parts: SceneKeyParts) =>
  items.findIndex((existing) => sceneKey(existing) === sceneKey(parts));

/**
 * Holds the current user's cart for the organization they're operating under. The cart is client
 * state backed by localStorage, so it stays in context rather than React Query.
 */
export const CartProvider: React.FC<
  React.PropsWithChildren<{userId: string | null; organizationId: string | null}>
> = ({userId, organizationId, children}) => {
  const initialCart = React.useMemo(
    () => readCart(userId, organizationId),
    [userId, organizationId]
  );
  // Switching org or user swaps carts on the same render, so no child ever sees the previous
  // scope's items.
  const [cart, setCart] = useStateWithDeps<CartV1>(initialCart, [userId, organizationId]);
  const [isPreviewOpen, setPreviewOpen] = React.useState(false);

  // The committed cart, which runs ahead of `cart` between a mutation and its re-render. Mutations
  // read it rather than the render they were called in, so several in one tick compose instead of
  // each overwriting the last. Synced before paint, so no event handler ever sees a stale one.
  const cartRef = React.useRef(cart);
  React.useLayoutEffect(() => {
    cartRef.current = cart;
  });

  const commit = React.useCallback(
    (update: (previous: CartV1) => CartV1) => {
      const next = update(cartRef.current);
      if (next === cartRef.current) {
        return;
      }
      cartRef.current = next;
      writeCart(userId, organizationId, next);
      setCart(next);
    },
    [setCart, userId, organizationId]
  );

  // Another tab's write is authoritative: whole-cart writes make last-write-wins the whole merge
  // policy, so re-reading is all this needs to do.
  React.useEffect(() => {
    if (!userId || !organizationId) {
      return;
    }
    const key = cartStorageKey(userId, organizationId);
    const onStorage = (event: StorageEvent) => {
      // A null key means storage was cleared wholesale.
      if (event.key !== null && event.key !== key) {
        return;
      }
      const adopted = readCart(userId, organizationId);
      // Adopt into the ref too, or the next mutation would compose against the pre-event cart and
      // write back items the other tab removed.
      cartRef.current = adopted;
      setCart(adopted);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId, organizationId, setCart]);

  const scopeKey = userId && organizationId ? cartStorageKey(userId, organizationId) : null;

  const value = React.useMemo<CartContextValue>(() => {
    const updateItems = (patches: CartItemPatch[]) => {
      if (patches.length === 0) {
        return;
      }
      // Repeated ids merge in call order, so a batch reads the same as the sequence it replaces.
      const merged = new Map<string, Partial<Omit<CartItemV1, 'id'>>>();
      for (const {id, patch} of patches) {
        merged.set(id, {...merged.get(id), ...patch});
      }
      commit((previous) => {
        let changed = false;
        const items = previous.items.map((item) => {
          const patch = merged.get(item.id);
          if (!patch) {
            return item;
          }
          changed = true;
          return {...item, ...patch};
        });
        return changed ? {...previous, items} : previous;
      });
    };

    const removeItems = (ids: string[]): CartItemRestore[] => {
      if (ids.length === 0) {
        return [];
      }
      const doomed = new Set(ids);
      // commit runs its update synchronously, so the entries it works out are read back here.
      const removed: CartItemRestore[] = [];
      commit((previous) => {
        const items: CartItemV1[] = [];
        for (const item of previous.items) {
          if (doomed.has(item.id)) {
            // The kept items ahead of it are its slot as of its own removal, which is the index
            // restoreItems asks for.
            removed.push({item, index: items.length});
          } else {
            items.push(item);
          }
        }
        return removed.length === 0 ? previous : {...previous, items};
      });
      return removed;
    };

    const restoreItems = (entries: CartItemRestore[]): number => {
      // commit runs its update synchronously, so the count it works out is read back here.
      let skippedFull = 0;
      commit((previous) => {
        const items = [...previous.items];
        const present = new Set(items.map(sceneKey));
        let restored = 0;

        // Newest removal first: undoing it rebuilds the list the next-older entry's index was
        // captured against, so each splice lands the item back in the slot it was removed from.
        for (let at = entries.length - 1; at >= 0; at--) {
          const entry = entries[at];
          const key = sceneKey(entry.item);
          // The scene came back some other way; undo wanted it in the cart, and it is.
          if (present.has(key)) {
            continue;
          }
          // Working backward, a full cart drops the earliest removals.
          if (items.length >= MAX_CART_ITEMS) {
            skippedFull++;
            continue;
          }
          // The cart can have changed since the removal, so the captured index only bounds where
          // the item goes back.
          items.splice(Math.min(Math.max(entry.index, 0), items.length), 0, entry.item);
          present.add(key);
          restored++;
        }

        return restored === 0 ? previous : {...previous, items};
      });
      return skippedFull;
    };

    return {
      items: cart.items,
      count: cart.items.length,
      isFull: cart.items.length >= MAX_CART_ITEMS,
      maxItems: MAX_CART_ITEMS,

      addItem: (item) => {
        const current = cartRef.current.items;
        if (indexOfScene(current, item) !== -1) {
          return 'already-in-cart';
        }
        if (current.length >= MAX_CART_ITEMS) {
          return 'cap-reached';
        }
        commit((previous) => ({...previous, items: [...previous.items, newItem(item)]}));
        return 'added';
      },


      updateItems,
      removeItems,
      restoreItems,
      updateItem: (id, patch) => updateItems([{id, patch}]),
      removeItem: (id) => removeItems([id]),

      clear: () => {
        commit((previous) => (previous.items.length === 0 ? previous : emptyCart()));
      },

      findBySceneKey: (parts) =>
        cart.items.find((existing) => sceneKey(existing) === sceneKey(parts)),

      scopeKey,
      isPreviewOpen,
      setPreviewOpen,
    };
  }, [cart, commit, scopeKey, isPreviewOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
